import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "./api.js";
import { ACTIVE_WORKSPACE_KEY } from "./transport.js";

/**
 * Which office the app is acting in.
 *
 * A person can belong to more than one office ("workspace"), and every request
 * already carries the active one as a header that transport.ts reads straight
 * from localStorage. This provider is the other half: it learns which offices
 * the viewer can act in, decides which is active, and hands the switcher a way
 * to change it.
 *
 * Kept beside branding rather than inside auth because the two share a shape —
 * both fetch once, both must degrade to a sensible default rather than block
 * render, and branding reads the active office's name from here.
 */

/** One office the viewer belongs to. Mirrors GET /workspaces' rows exactly. */
export type Workspace = {
  id: string;
  name: string;
  slug: string;
  role: string;
  isDefault: boolean;
};

type WorkspaceResponse = { workspaces: Workspace[]; activeId: string | null };

type WorkspaceContextValue = {
  workspaces: Workspace[];
  activeId: string | null;
  /** The active office resolved to its row, or null before the list loads. */
  active: Workspace | null;
  loading: boolean;
  /** Switch office: persists the choice and reloads so every query re-reads
   *  the header. A no-op when the id is already active. */
  setActive: (id: string) => void;
  /**
   * Open something that may live in another office: switch to `officeId`
   * first when it is a different office the viewer belongs to, then `go`.
   * `go` runs before the reload, so the reload lands on the destination;
   * without a switch it is simply called.
   */
  openIn: (officeId: string | null | undefined, go: () => void) => void;
};

const WorkspaceContext = createContext<WorkspaceContextValue>({
  workspaces: [],
  activeId: null,
  active: null,
  loading: true,
  setActive: () => {},
  openIn: (_officeId, go) => go(),
});

/** Read the stored active id, tolerating a browser that throws on access. */
function readStored(): string | null {
  try {
    return localStorage.getItem(ACTIVE_WORKSPACE_KEY);
  } catch {
    return null;
  }
}

/** Persist the active id, mirroring transport.ts's swallow-on-failure idiom. */
function writeStored(id: string): void {
  try {
    localStorage.setItem(ACTIVE_WORKSPACE_KEY, id);
  } catch {
    // Storage is best-effort. A locked-down browser that refuses the write
    // still gets the correct office for this render; it just will not
    // remember the switch across a reload, which is the same as never having
    // switched — no worse than the pre-office status quo.
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get<WorkspaceResponse>("/workspaces")
      .then((res) => {
        if (cancelled) return;
        setWorkspaces(res.workspaces);

        /*
         * Which office is active, in order of preference:
         *   1. the stored choice, *if* the viewer still belongs to it — a
         *      stale id (an office they were removed from) must not stick, or
         *      every request keeps sending a header the server rejects;
         *   2. the server's activeId;
         *   3. the office marked default;
         *   4. whatever is first.
         * Then write the resolved id back so the header transport.ts reads and
         * this provider agree before any switch.
         */
        const stored = readStored();
        const known = new Set(res.workspaces.map((w) => w.id));
        const resolved =
          (stored && known.has(stored) ? stored : null) ??
          (res.activeId && known.has(res.activeId) ? res.activeId : null) ??
          res.workspaces.find((w) => w.isDefault)?.id ??
          res.workspaces[0]?.id ??
          null;

        setActiveId(resolved);
        if (resolved) writeStored(resolved);
      })
      .catch(() => {
        // No session yet (the login screen), or the fetch failed: leave the
        // list empty so the switcher hides and branding falls back to global,
        // exactly as BrandingProvider swallows its own failure.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const active = useMemo(
    () => workspaces.find((w) => w.id === activeId) ?? null,
    [workspaces, activeId],
  );

  const setActive = useCallback(
    (id: string) => {
      if (id === activeId) return;
      writeStored(id);
      // The deliberately-simple approach the brief names: a full reload, so
      // every query re-reads the office header rather than being invalidated
      // one by one. In-memory UI state is discarded, which is acceptable for
      // a change this deliberate.
      window.location.reload();
    },
    [activeId],
  );

  const openIn = useCallback(
    (officeId: string | null | undefined, go: () => void) => {
      // Only switch to an office the viewer is known to belong to: switching
      // to anything else would store an id the server rejects on every request.
      if (!officeId || officeId === activeId || !workspaces.some((w) => w.id === officeId)) {
        go();
        return;
      }
      writeStored(officeId);
      // Navigate first (the router updates the URL synchronously), then reload
      // as setActive does, so the page comes back up on the destination with
      // every query reading the new office.
      go();
      window.location.reload();
    },
    [activeId, workspaces],
  );

  const value = useMemo(
    () => ({ workspaces, activeId, active, loading, setActive, openIn }),
    [workspaces, activeId, active, loading, setActive, openIn],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  return useContext(WorkspaceContext);
}
